import { ImageIcon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, ErrorNotice, Panel, Pill, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage, getJson, sendJson } from '@/lib/api';

/** One partner photo (GET /organizations/:id/partner-photos). */
interface StaffPartnerPhoto {
  readonly id: string;
  readonly programId: string;
  readonly programName: string | null;
  readonly url: string;
  readonly caption: string | null;
  readonly hidden: boolean;
  readonly createdAt: string;
}

const shortDate = (iso: string): string => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * A partner's own activity photos. They go live on the showcase the moment
 * the partner adds them, so staff get a Hide here to take one down at once.
 */
export function OrgPartnerPhotosPanel({ organizationId, organizationName }: { readonly organizationId: string; readonly organizationName: string }): React.ReactElement {
  const queryClient = useQueryClient();
  const key = ['partner-photos', organizationId] as const;
  const photos = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => getJson<{ photos: StaffPartnerPhoto[] }>(`/organizations/${organizationId}/partner-photos`, undefined, signal),
  });
  const setHidden = useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: boolean }) =>
      sendJson('patch', `/organizations/${organizationId}/partner-photos/${id}`, { hidden }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const rows = photos.data?.photos ?? [];

  return (
    <div className="mt-8 space-y-4">
      <div>
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">Partner photos</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          Photos {organizationName} added from events at their venue. An activity with photos shows only theirs on the showcase. They go live when added; hide
          one to take it down.
        </p>
      </div>

      {setHidden.isError ? <ErrorNotice inline title="Could not update the photo" message={errorMessage(setHidden.error)} /> : null}

      {photos.isPending ? (
        <SkeletonTable rows={2} columns={['w-[30%]', 'w-[30%]', 'w-[30%]']} label="Loading photos…" />
      ) : photos.isError ? (
        <ErrorNotice title="Photos unavailable" message={errorMessage(photos.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<ImageIcon aria-hidden className="size-6" />}
            title="No partner photos yet"
            message="When the partner adds photos from their portal, they appear here. Until then their showcase uses ours."
          />
        </Panel>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {rows.map((photo) => (
            <li key={photo.id} className="overflow-hidden rounded-portal-card border border-portal-line bg-white">
              <img src={photo.url} alt={photo.caption ?? photo.programName ?? 'Partner photo'} loading="lazy" className={`aspect-[4/3] w-full object-cover ${photo.hidden ? 'opacity-40' : ''}`} />
              <div className="space-y-2 p-3">
                <p className="text-portal-small font-bold">{photo.programName ?? 'An activity no longer offered'}</p>
                {photo.caption ? <p className="text-portal-small">{photo.caption}</p> : null}
                <p className="text-portal-micro text-portal-muted">Added {shortDate(photo.createdAt)}</p>
                <div className="flex items-center justify-between gap-2">
                  {photo.hidden ? <Pill tone="warning">Hidden</Pill> : <Pill tone="success">On the showcase</Pill>}
                  <Button
                    variant="secondary"
                    size="compact"
                    disabled={setHidden.isPending}
                    onClick={() => setHidden.mutate({ id: photo.id, hidden: !photo.hidden })}
                  >
                    {photo.hidden ? 'Show' : 'Hide'}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
